import {
  createSentenceUtterances,
  SentenceBoundary
} from './text-util'
import type { BrowserType, DeviceType } from './app-type-util'
import {
  detectDeviceType, detectBrowserType, isVoiceSpeechSupported
} from './app-type-util'
import {
  createChunkAlignment,
  displayWordIndexForSpokenCharIndex,
  type ChunkAlignment
} from './speech-alignment'
import {
  resolveSpeechLanguage,
  speechLanguageTag,
  voiceMatchesLanguage,
  voiceTagIsExact,
  SPEECH_LANGUAGE_AUTO,
  type SpeechLanguage,
  type SpeechLanguageSetting
} from './speech-language'

// ============================================================================
// SPEECH SPEED CONFIGURATION
// ============================================================================
/**
 * Where the speed control starts, for a reader who has not moved it.
 *
 * One value for every device, and the engine's own natural rate. There used to be
 * three - 0.9 on a desktop, 0.8 on Android, 0.75 on iOS - on the reasoning that
 * the same number sounds faster on some mobile engines. With no way to adjust the
 * speed that was a defensible guess; now that there is one, it is a hidden offset
 * that makes the same document read at a different pace depending on what it is
 * opened on, and makes "1,0x" on the screen mean three different things.
 */
export const DEFAULT_SPEECH_RATE = 1.0

export const MIN_SPEECH_RATE = 0.5
export const MAX_SPEECH_RATE = 2.0

/**
 * How far one press of the speed control moves.
 *
 * A tenth. Reading speed is a close-fitting preference - the difference between
 * comfortable and too fast is smaller than a quarter step - so the range is worth
 * more presses to cross. stepSpeechRate snaps onto this grid, so a rate saved by
 * an earlier coarser step lands back on it.
 */
export const SPEECH_RATE_STEP = 0.1

const VOICE_AUTO_VALUE = 'auto'

/**
 * Engines that sound robotic enough to be worse than no speech at all. Kept out
 * of automatic selection; a reader who picks one by hand still gets it.
 */
const VOICE_BLACKLIST_PATTERNS = [/espeak/i, /mbrola/i, /festival/i]

/**
 * Names the platforms give their good voices.
 *
 * These outrank localService on purpose. On Windows the locally installed Polish
 * voice ("Paulina") sits next to the far better network voices Edge exposes
 * ("Zofia Natural", "Marek Natural"), and the previous scoring preferred the
 * local one - it added points for localService and nothing for these names, so
 * the app systematically chose the worse of the two voices it had.
 */
const HIGH_QUALITY_NAME_PATTERNS = [/natural/i, /neural/i, /online/i, /premium/i, /enhanced/i]

/**
 * Google's own voices, which the client asked to hear by default.
 *
 * "Google polski" is what Chrome and Android's TTS engine ship, and it is the
 * voice the client was comparing everything else against. With the voice picker
 * gone (DYS-044) that preference has to live in the scoring.
 */
const GOOGLE_VOICE_PATTERN = /google/i

/** Well-regarded voices per language, as a tie-breaker between equals. */
const PREFERRED_VOICE_NAMES: Record<SpeechLanguage, RegExp[]> = {
  pl: [/zofia/i, /marek/i, /paulina/i, /ewa/i],
  en: [/aria/i, /jenny/i, /samantha/i, /daniel/i],
  de: [/katja/i, /conrad/i, /anna/i, /markus/i],
}

/**
 * Languages whose phonetics are close enough to read another language's text
 * intelligibly, used only when the device has no voice for the real one.
 *
 * This exists because iOS may ship without a Polish voice at all, and a Czech
 * voice reading Polish is imperfect but understandable, where an American English
 * voice reading Polish is not. It is a last resort, not a preference.
 */
const PHONETIC_NEIGHBOURS: Record<SpeechLanguage, string[]> = {
  pl: ['cs', 'sk'],
  de: ['nl'],
  en: [],
}

// ============================================================================
export interface VoiceSpeechCallbacks {
  setState: (state: VoiceSpeechState) => void
  debugLog?: (message: string) => void
}

export interface VoiceSpeechState {
  isPlaying: boolean
  isPaused: boolean
  /**
   * Index of the word being spoken, counted across the whole document, or -1
   * when the engine has not told us. See wordHighlightAvailable.
   */
  currentWordIndex: number
  /** Index of the chunk being spoken - one utterance, one highlight unit. */
  currentSentenceIndex: number
  currentDisplayText: string
  /** The language actually in use, after resolving an automatic setting. */
  language: SpeechLanguage
  /**
   * Whether this engine reports word boundaries, so the reader can be shown a
   * word-level highlight. False until the first boundary event arrives, and it
   * never arrives on some engines (iOS Safari, parts of Chrome on Android) -
   * there the chunk highlight is all there is.
   */
  wordHighlightAvailable: boolean
  rate: number
}

/** The starting speed, for a screen with none saved yet. */
export function getDefaultSpeechRate(): number {
  return DEFAULT_SPEECH_RATE
}

/** Rounds to hundredths, so a rate built up by stepping stays a clean number. */
function roundRate(rate: number): number {
  return Math.round(rate * 100) / 100
}

export function clampSpeechRate(rate: number): number {
  if (!Number.isFinite(rate)) return DEFAULT_SPEECH_RATE
  return roundRate(Math.min(MAX_SPEECH_RATE, Math.max(MIN_SPEECH_RATE, rate)))
}

/**
 * The next speed up or down from here.
 *
 * Snapped onto the step grid rather than simply added to, so a rate saved by the
 * slider this replaced - 0.9, say - lands on 1.0 and 0.75 rather than carrying its
 * offset up and down the range forever.
 */
export function stepSpeechRate(rate: number, direction: 1 | -1): number {
  const current = clampSpeechRate(rate)
  const exactSteps = current / SPEECH_RATE_STEP
  const nearestSteps = Math.round(exactSteps)

  // On the grid already: move one step. Off it: move onto the next grid position
  // in the direction asked for, so the offset is corrected by the first press.
  const nextSteps =
    roundRate(nearestSteps * SPEECH_RATE_STEP) === current
      ? nearestSteps + direction
      : direction > 0
        ? Math.ceil(exactSteps)
        : Math.floor(exactSteps)

  return clampSpeechRate(nextSteps * SPEECH_RATE_STEP)
}

// ============================================================================

export class VoiceSpeechManager {
  private callbacks: VoiceSpeechCallbacks
  private debugEnabled: boolean
  private cachedVoice: SpeechSynthesisVoice | null = null
  /** Which language the cached voice was chosen for; null when there is no cache. */
  private cachedVoiceLanguage: SpeechLanguage | null = null
  private preferredVoiceURI: string | null = null

  // Store state internally
  private state: VoiceSpeechState

  // Cache device type and speech rate
  private deviceType: DeviceType
  private speechRate: number
  private browserType: BrowserType

  /** Which language the reader asked for: a fixed one, or automatic detection. */
  private languageSetting: SpeechLanguageSetting = SPEECH_LANGUAGE_AUTO

  // Simplified sentence tracking
  private sentences: SentenceBoundary[] = []

  /**
   * Display-to-spoken word alignment per chunk, built on first boundary event.
   * Cleared with the chunk list, since it is keyed by chunk index.
   */
  private alignments: Map<number, ChunkAlignment> = new Map()

/**
   * The utterances handed to the engine and not finished yet, held so that they
   * are not garbage collected mid-speech - a Chromium utterance that is collected
   * while speaking never fires its `end` event, which would strand playback.
   */
  private pendingUtterances: SpeechSynthesisUtterance[] = []

  /**
   * Index of the last chunk handed to the engine.
   *
   * Not the same as the chunk being spoken: one chunk is always queued behind it.
   * See queueAhead.
   */
  private queuedIndex: number = -1

  // Monotonic session id used to ignore stale speech callbacks
  private speechSessionId: number = 0

  constructor(
    initialState: VoiceSpeechState,
    callbacks: VoiceSpeechCallbacks,
    debugEnabled: boolean = false
  ) {
    this.state = { ...initialState }
    this.callbacks = callbacks
    this.debugEnabled = debugEnabled

    // Detect device capabilities
    this.deviceType = detectDeviceType()
    this.speechRate = clampSpeechRate(initialState.rate || DEFAULT_SPEECH_RATE)
    this.browserType = detectBrowserType()

    this.state.rate = this.speechRate
    this.state.language = resolveSpeechLanguage(this.languageSetting, this.state.currentDisplayText)
  }

  setPreferredVoiceURI(voiceURI: string | null) {
    if (this.preferredVoiceURI === voiceURI) {
      return
    }
    this.preferredVoiceURI = voiceURI
    this.invalidateVoiceCache()
    this.restartCurrentChunkIfSpeaking('voice changed')
  }

  /**
   * Sets the language, or 'auto' to detect it from the text.
   *
   * The resolved language decides three things at once: the utterance's lang tag,
   * which voices are candidates, and whether the Polish number and abbreviation
   * rules in cleanTextForTTS are allowed to run.
   */
  setLanguageSetting(setting: SpeechLanguageSetting) {
    if (this.languageSetting === setting) {
      return
    }
    this.languageSetting = setting
    this.invalidateVoiceCache()

    const language = resolveSpeechLanguage(setting, this.state.currentDisplayText)
    const languageChanged = language !== this.state.language
    this.updateState({ language })

    if (languageChanged) {
      // The chunks hold TTS text cleaned for the old language, so they have to go.
      this.restartCurrentChunkIfSpeaking('language changed', true)
    }
  }

  /**
   * Forgets the chosen voice, so the next chunk picks again from the current list.
   *
   * getVoices() fills in asynchronously, and on some engines it fills in twice:
   * Chrome adds its network voices and Edge its Online Natural ones after the
   * first read. Without this the voice cached from the short early list held for
   * the rest of the session - including when the list that arrives late is the one
   * containing the voice the reader had actually saved.
   *
   * Deliberately does not restart the current chunk. The list changing is not
   * something the reader did, and interrupting them for it would be startling; the
   * new voice takes over within a chunk or two.
   */
  refreshVoices() {
    this.invalidateVoiceCache()
  }

  setRate(rate: number) {
    const clamped = clampSpeechRate(rate)
    if (clamped === this.speechRate) {
      return
    }
    this.speechRate = clamped
    this.updateState({ rate: clamped })
    // Web Speech cannot change the rate of an utterance already in flight, so the
    // current chunk starts over. That is a short audible jump, and it is what
    // makes the control feel connected to what the reader hears.
    this.restartCurrentChunkIfSpeaking('rate changed')
  }

  getRate(): number {
    return this.speechRate
  }

  handlePlayPause() {
    // Check if voice speech is supported before proceeding
    if (!isVoiceSpeechSupported()) {
      this.debug('Voice speech not supported in this browser')
      return
    }

    if (this.state.isPlaying) {
      this.pause()
      return
    } else if (this.resume()) {
      return
    }
    this.startNewSpeech(true) // true = reset to beginning
  }

  /** Moves to the next chunk, or stops if the document has ended. */
  skipToNextChunk() {
    if (this.sentences.length === 0) {
      return
    }

    const next = this.state.currentSentenceIndex + 1
    if (next >= this.sentences.length) {
      this.stop()
      return
    }

    this.jumpToChunk(next)
  }

  /**
   * Moves to the previous chunk, or restarts the current one.
   *
   * Restarting on the first chunk matches how every audio player behaves and is
   * also the more useful of the two answers here - a reader who missed a phrase
   * presses back to hear it again.
   */
  skipToPreviousChunk() {
    if (this.sentences.length === 0) {
      return
    }

    const previous = Math.max(0, this.state.currentSentenceIndex - 1)
    this.jumpToChunk(previous)
  }

  /**
   * Starts reading at `index`, from wherever the playback currently stands.
   *
   * This is what a tap on a sentence calls, and it is the one thing the transport
   * buttons could not do: they move the position of a reading already under way,
   * while a tap also has to be able to begin one. A reader who has stopped, or
   * who paused two pages ago, taps a line and expects to hear that line.
   *
   * The index is clamped rather than refused. It reaches us from a DOM attribute
   * written by the render that produced this chunk list, so the two normally
   * agree; clamping is what keeps the one case where they do not - an edit that
   * shortened the text between the render and the tap - from turning into no
   * speech at all, with nothing on screen to explain why.
   */
  startFromChunk(index: number) {
    if (!isVoiceSpeechSupported()) {
      this.debug('Voice speech not supported in this browser')
      return
    }

    if (!Number.isFinite(index)) {
      return
    }

    // The chunk list is built lazily by startNewSpeech, and stop() throws it
    // away, so a tap arriving from a standing stop has nothing to clamp against
    // yet. Building it here is the same work startNewSpeech would do a moment
    // later, done early enough to be counted.
    if (this.sentences.length === 0) {
      this.sentences = createSentenceUtterances(this.state.currentDisplayText, this.state.language)
      this.alignments.clear()
    }

    if (this.sentences.length === 0) {
      this.debug('No sentences to speak')
      return
    }

    const clamped = Math.min(Math.max(Math.trunc(index), 0), this.sentences.length - 1)
    this.updateState({ currentSentenceIndex: clamped, currentWordIndex: -1 })
    this.startNewSpeech(false)
  }

  /**
   * Where the transport buttons land.
   *
   * The same move as startFromChunk, with the one difference that a skip may not
   * start anything: pressing next on a document nobody is reading moves the
   * position the next play will start from, and stays silent.
   */
  private jumpToChunk(index: number) {
    if (this.state.isPlaying || this.state.isPaused) {
      this.startFromChunk(index)
      return
    }

    this.updateState({ currentSentenceIndex: index, currentWordIndex: -1 })
  }

  // Public method to stop speech completely (used when navigating away or refreshing)
  // This is different from pause - stop cannot be resumed
  stop() {
    // Invalidate any in-flight utterance callbacks
    this.speechSessionId += 1

    // Multiple attempts to ensure speech is stopped
    try {
      speechSynthesis.cancel()
    } catch (error) {
      console.warn('Error canceling speech synthesis:', error)
    }

    // Force stop any ongoing speech
    try {
      speechSynthesis.pause()
      speechSynthesis.cancel()
    } catch (error) {
      console.warn('Error force-stopping speech synthesis:', error)
    }

    this.clearQueue()
    this.sentences = []
    this.alignments.clear()

    // Update UI state - clear paused state since this is a stop, not a pause
    this.updateState({
      isPlaying: false,
      isPaused: false,
      currentWordIndex: -1,
      currentSentenceIndex: -1
    })
  }

  // Helper method to update state and notify component
  private updateState(updates: Partial<VoiceSpeechState>) {
    this.state = { ...this.state, ...updates }
    this.callbacks.setState(this.state)
  }

  // Public method to update the display text
  updateDisplayText(newText: string) {
    this.alignments.clear()
    this.sentences = []
    this.invalidateVoiceCache()
    this.updateState({
      currentDisplayText: newText,
      language: resolveSpeechLanguage(this.languageSetting, newText)
    })
  }

  private invalidateVoiceCache() {
    this.cachedVoiceLanguage = null
    this.cachedVoice = null
  }

  /**
   * Applies a setting change to what the reader is hearing right now.
   *
   * `rebuildChunks` is for changes that alter the text handed to the engine
   * rather than just how it is spoken - only a language change does that, via
   * cleanTextForTTS - so the cached chunks and alignments have to be thrown away.
   */
  private restartCurrentChunkIfSpeaking(reason: string, rebuildChunks: boolean = false) {
    if (rebuildChunks) {
      this.sentences = []
      this.alignments.clear()
    }

    if (!this.state.isPlaying) {
      return
    }

    this.debug(`Restarting current chunk: ${reason}`)
    this.startNewSpeech(false)
  }

  private isVoiceBlacklisted(voice: SpeechSynthesisVoice): boolean {
    return VOICE_BLACKLIST_PATTERNS.some((pattern) => pattern.test(voice.name))
  }

  private scoreVoice(voice: SpeechSynthesisVoice, language: SpeechLanguage): number {
    let score = 0
    const name = voice.name.toLowerCase()

    if (voiceTagIsExact(voice, language)) score += 120
    else if (voiceMatchesLanguage(voice, language)) score += 100

    /*
     * Enough to outrank every other bonus stacked together, and no more.
     *
     * The rival this has to beat is real: on Windows Chrome, "Google polski" sits
     * next to "Microsoft Zofia Online (Natural) - Polish (Poland)", which takes
     * +24 for Natural and another +12 for being in PREFERRED_VOICE_NAMES.pl. The
     * obvious +30 loses to it, 150 to 156. Clearing 36 is the requirement; 40
     * leaves room for one more tie-breaker without needing this revisited.
     *
     * The guarantee is only between voices tagged equally well. A Google voice
     * that reports Polish as Android's three-letter "pol" scores +100 rather than
     * +120, and a 20-point deficit plus a fully stacked rival still beats +40.
     * Covering that too would need to clear 56, which would let a badly tagged
     * Google voice win over a correctly tagged native one - a worse rule than
     * losing this case, so it is left to lose.
     */
    if (GOOGLE_VOICE_PATTERN.test(name)) score += 40

    if (HIGH_QUALITY_NAME_PATTERNS.some((pattern) => pattern.test(name))) score += 24
    if (PREFERRED_VOICE_NAMES[language]?.some((pattern) => pattern.test(name))) score += 12
    if (voice.localService) score += 4
    if (voice.default) score += 2

    if (this.isVoiceBlacklisted(voice)) score -= 1000

    return score
  }

  /** Best voice for the language, or null when the device has none. */
  private getVoiceForLanguage(
    voices: SpeechSynthesisVoice[],
    language: SpeechLanguage
  ): SpeechSynthesisVoice | null {
    const candidates = voices
      .filter((voice) => voiceMatchesLanguage(voice, language))
      .filter((voice) => !this.isVoiceBlacklisted(voice))
      .sort((a, b) => this.scoreVoice(b, language) - this.scoreVoice(a, language))

    return candidates[0] ?? null
  }

  private getNeighbourVoice(
    voices: SpeechSynthesisVoice[],
    language: SpeechLanguage
  ): SpeechSynthesisVoice | null {
    for (const neighbour of PHONETIC_NEIGHBOURS[language] ?? []) {
      const voice = voices.find(
        (candidate) =>
          candidate.lang.toLowerCase().startsWith(neighbour) && !this.isVoiceBlacklisted(candidate)
      )
      if (voice) {
        this.debug(`No ${language} voice; falling back to ${voice.name} (${voice.lang})`)
        return voice
      }
    }
    return null
  }

  private highlightChunk(chunkIndex: number) {
    if (chunkIndex < 0 || chunkIndex >= this.sentences.length) {
      this.debug(`Invalid sentence index: ${chunkIndex}`)
      return
    }

    const chunk = this.sentences[chunkIndex]

    this.updateState({
      currentSentenceIndex: chunkIndex,
      // Only claim a word position on engines that have already proved they
      // report one; elsewhere -1 tells the reader to highlight the chunk instead
      // of pinning the highlight to a first word that would never advance.
      currentWordIndex: this.state.wordHighlightAvailable ? chunk.startWordIndex : -1,
      isPlaying: true,
      isPaused: false
    })
  }

  private getAlignment(chunkIndex: number): ChunkAlignment | null {
    const chunk = this.sentences[chunkIndex]
    if (!chunk) return null

    const cached = this.alignments.get(chunkIndex)
    if (cached) return cached

    const alignment = createChunkAlignment(chunk.text, chunk.ttsText)
    this.alignments.set(chunkIndex, alignment)
    return alignment
  }

  /**
   * Moves the highlight onto the word the engine has just started speaking.
   */
  private handleWordBoundary(chunkIndex: number, charIndex: number) {
    const chunk = this.sentences[chunkIndex]
    const alignment = this.getAlignment(chunkIndex)
    if (!chunk || !alignment) return

    const wordInChunk = displayWordIndexForSpokenCharIndex(alignment, charIndex)

    this.updateState({
      wordHighlightAvailable: true,
      currentSentenceIndex: chunkIndex,
      currentWordIndex: chunk.startWordIndex + wordInChunk
    })
  }

  private setupSimpleHandlers(
    utterance: SpeechSynthesisUtterance,
    sentenceIndex: number,
    sessionId: number
  ) {
    // Simple start handler
    utterance.onstart = () => {
      if (sessionId !== this.speechSessionId) {
        this.debug(`Ignoring stale onstart for sentence ${sentenceIndex} (session ${sessionId})`)
        return
      }
      // Ensure highlighting is in sync when speech actually starts
      this.highlightChunk(sentenceIndex)
    }

    /*
     * Word-level highlighting comes from here.
     *
     * Support is uneven: Chromium desktop and Firefox report every word, while
     * iOS Safari and Chrome on Android with the Google engine report nothing at
     * all. There is no capability to query, so the reader gets the chunk
     * highlight until the first event proves otherwise.
     *
     * `name` is 'word' or 'sentence' where it is set at all; engines that leave
     * it undefined only ever emit word boundaries, so those are taken as words.
     */
    utterance.onboundary = (event: SpeechSynthesisEvent) => {
      if (sessionId !== this.speechSessionId) {
        return
      }
      if (event.name && event.name !== 'word') {
        return
      }
      this.handleWordBoundary(sentenceIndex, event.charIndex ?? 0)
    }

    // Simple end handler - keep the queue one chunk ahead
    utterance.onend = () => {
      this.handleSpeechEnd(sentenceIndex, sessionId, utterance)
    }

    // Error handler
    utterance.onerror = (event) => {
      if (sessionId !== this.speechSessionId) {
        this.debug(`Ignoring stale onerror for sentence ${sentenceIndex} (session ${sessionId})`)
        return
      }
      this.debug(`ERROR in sentence ${sentenceIndex}: ${event.error}`)
      this.handleSpeechError(event, sentenceIndex, sessionId, utterance)
    }
  }

  private debug(message: string) {
    if (!this.debugEnabled) {
      return
    }
    const logMessage = `[VoiceSpeech] ${message}`
    console.log(logMessage)
    if (this.callbacks.debugLog) {
      this.callbacks.debugLog(logMessage)
    }
  }

  private pause() {
    /*
     * The session moves on before the engine is touched.
     *
     * iOS pauses by cancelling, and cancelling now ends two utterances rather
     * than one - the chunk being spoken and the chunk queued behind it. The flag
     * this replaces was a single boolean consumed by the first `end` event, so
     * the second one would have fallen through and queued the next chunk, and
     * pausing would have carried on reading. A session bump invalidates every
     * pending callback at once, however many are outstanding.
     */
    this.speechSessionId += 1

    // On iOS, we need to cancel instead of pause since pause doesn't work reliably
    if (this.deviceType === 'ios') {
      this.debug("iOS: using cancel instead of pause")
      speechSynthesis.cancel()
    } else {
      // Non-iOS: use standard pause
      speechSynthesis.pause()
    }

    this.clearQueue()

    this.updateState({
      isPlaying: false,
      isPaused: true
    })
  }

  private loadVoices(): SpeechSynthesisVoice[] {
    let voices = speechSynthesis.getVoices()

    // iOS-specific voice loading - iOS Safari often needs multiple attempts and delays
    if (this.deviceType === 'ios' && voices.length === 0) {
      // Force voice loading on iOS by creating a dummy utterance (silent, never audible)
      const dummyUtterance = new SpeechSynthesisUtterance('')
      dummyUtterance.lang = speechLanguageTag(this.state.language)
      dummyUtterance.volume = 0
      speechSynthesis.speak(dummyUtterance)
      speechSynthesis.cancel()

      voices = speechSynthesis.getVoices()

      if (voices.length === 0) {
        this.debug(`iOS: No voices loaded after retry`)
      }
    }

    // Edge-specific voice loading
    if (voices.length === 0 && this.browserType === 'edge') {
      speechSynthesis.cancel()
      voices = speechSynthesis.getVoices()
    }

    // Safari/Brave/Firefox voice loading
    if (
      voices.length === 0 &&
      (this.browserType === 'safari' || this.browserType === 'brave' || this.browserType === 'firefox')
    ) {
      speechSynthesis.cancel()
      voices = speechSynthesis.getVoices()

      if (voices.length === 0) {
        this.debug(`${this.browserType}: No voices loaded, speech may not work`)
      }
    }

    return voices
  }

  private selectVoice(language: SpeechLanguage): SpeechSynthesisVoice | null {
    // The cache is keyed by language: the same manager reads a Polish document
    // and then an English one, and getVoices() is expensive enough on some
    // engines to be worth caching but not worth answering the wrong question.
    if (this.cachedVoice && this.cachedVoiceLanguage === language) {
      return this.cachedVoice
    }

    const voices = this.loadVoices()
    this.debug(`Available voices count: ${voices.length}`)

    const remember = (voice: SpeechSynthesisVoice | null): SpeechSynthesisVoice | null => {
      if (voice) {
        this.cachedVoice = voice
        this.cachedVoiceLanguage = language
      }
      return voice
    }

    if (this.preferredVoiceURI && this.preferredVoiceURI !== VOICE_AUTO_VALUE) {
      const preferredVoice = voices.find((voice) => voice.voiceURI === this.preferredVoiceURI)
      if (preferredVoice) {
        this.debug(`Using user selected voice: ${preferredVoice.name} (${preferredVoice.lang})`)
        return remember(preferredVoice)
      }
      this.debug('Saved voice not available, falling back to automatic ranking')
    }

    const languageVoice = this.getVoiceForLanguage(voices, language)
    if (languageVoice) {
      this.debug(`Selected ${language} voice: ${languageVoice.name} (${languageVoice.lang})`)
      return remember(languageVoice)
    }

    const neighbourVoice = this.getNeighbourVoice(voices, language)
    if (neighbourVoice) {
      return remember(neighbourVoice)
    }

    const fallbackVoice = voices.find((voice) => !this.isVoiceBlacklisted(voice))
    if (fallbackVoice) {
      this.debug(`Using fallback voice: ${fallbackVoice.name} (${fallbackVoice.lang})`)
      return remember(fallbackVoice)
    }

    this.debug("No voices available")
    return null
  }

  private createUtterance(text: string, rate: number): SpeechSynthesisUtterance {
    const language = this.state.language
    const utterance = new SpeechSynthesisUtterance(text)
    utterance.rate = rate
    utterance.pitch = 1
    utterance.volume = 1
    utterance.lang = speechLanguageTag(language)

    const voice = this.selectVoice(language)
    if (voice) {
      utterance.voice = voice

      /*
       * A voice's own tag is more reliable than the generic one, especially on
       * iOS, so it wins when it is a voice for the language we want. When the
       * only voice available is a phonetic stand-in - a Czech voice reading
       * Polish - its tag is kept too: asking a cs-CZ voice to speak pl-PL makes
       * some engines silently swap in a different voice, which is how a Polish
       * document ended up being read by an English one.
       */
      utterance.lang = voice.lang || utterance.lang
    }

    return utterance
  }

  private debugSentence(sentenceIndex: number) {
    if (!this.debugEnabled) {
      return
    }
    if (sentenceIndex < 0 || sentenceIndex >= this.sentences.length) {
      this.debug(`debugSentence: invalid sentence index ${sentenceIndex}`)
      return
    }
    const sentence = this.sentences[sentenceIndex]
    // Accessing ttsText here will trigger lazy cleaning via cleanTextForTTS
    const original = sentence.text
    const cleaned = sentence.ttsText
    this.debug(
      `Sentence ${sentenceIndex}: original="${original}" | ttsText="${cleaned}"`
    )
  }

  private clearQueue() {
    this.pendingUtterances = []
    this.queuedIndex = -1
  }

  /**
   * Hands one chunk to the engine. Returns false if it could not be started.
   */
  private speakChunk(index: number): boolean {
    const chunk = this.sentences[index]
    if (!chunk) {
      return false
    }

    this.debugSentence(index)
    const utterance = this.createUtterance(chunk.ttsText, this.speechRate)
    this.setupSimpleHandlers(utterance, index, this.speechSessionId)

    try {
      this.pendingUtterances.push(utterance)
      speechSynthesis.speak(utterance)
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error)
      this.debug(`ERROR: Speech failed for chunk ${index}: ${errorMsg}`)
      console.error('Failed to start speech synthesis:', error)
      this.pendingUtterances = this.pendingUtterances.filter((pending) => pending !== utterance)
      return false
    }

    this.queuedIndex = index
    // Don't highlight here - let the onstart handler do it for consistency
    return true
  }

  /**
   * Keeps one chunk queued behind the one being spoken.
   *
   * The next utterance used to be created and spoken from the `end` handler of the
   * previous one, so every chunk boundary cost a round trip out to JavaScript and
   * back into the engine - audible as a gap, and heard more often now that a long
   * sentence is split into phrases. Handing the engine the following chunk while
   * the current one is still playing lets it run them together, which is what its
   * own queue is for.
   *
   * One ahead rather than several: each queued utterance is text already committed
   * to the engine, and everything that changes how the text sounds - the speed, the
   * voice, the language, a skip - has to cancel and re-queue whatever is
   * outstanding. One is enough to close the gap and cheap to throw away.
   */
  private queueAhead() {
    const next = this.queuedIndex + 1
    if (next < this.sentences.length) {
      this.speakChunk(next)
    }
  }

  private handleSpeechEnd(
    endedIndex: number,
    sessionId: number,
    finished?: SpeechSynthesisUtterance
  ) {
    if (sessionId !== this.speechSessionId) {
      this.debug(`Ignoring stale onend callback (session ${sessionId})`)
      return
    }

    this.pendingUtterances = this.pendingUtterances.filter((pending) => pending !== finished)

    /*
     * The end of the last chunk is the end of the document.
     *
     * Which chunk ended has to come from the utterance rather than from
     * currentSentenceIndex: with one chunk queued ahead, the state has already
     * moved on to the chunk now being spoken by the time this runs.
     */
    if (endedIndex >= this.sentences.length - 1) {
      this.clearQueue()
      this.updateState({
        isPlaying: false,
        isPaused: false,
        currentWordIndex: -1,
        currentSentenceIndex: -1
      })
      this.sentences = []
      this.alignments.clear()
      return
    }

    // The chunk after the one already queued behind this one.
    this.queueAhead()
  }

  private handleSpeechError(
    event: SpeechSynthesisErrorEvent,
    sentenceIndex: number,
    sessionId: number,
    utterance?: SpeechSynthesisUtterance
  ) {
    // "interrupted" and "canceled" are normal conditions when speech is cancelled, not real errors
    const errorType = event.error
    const isNormalCancellation = errorType === 'interrupted' || errorType === 'canceled'

    if (!isNormalCancellation) {
      const errorInfo = errorType ? `Error: ${errorType}` : 'Unknown error'
      console.error(`Speech error (sentence ${sentenceIndex}): ${errorInfo}`, event)
      this.debug(`Speech error (sentence ${sentenceIndex}): ${errorInfo}`)
    }

    this.handleSpeechEnd(sentenceIndex, sessionId, utterance)
  }

  private resume(): boolean {
    if (!this.state.isPaused) {
      return false
    }
    // Web Speech cannot start an utterance from the middle, so resuming replays
    // the chunk that was interrupted. Chunks are phrase-sized, so this repeats a
    // few words rather than a whole paragraph.
    this.startNewSpeech(false)
    return true
  }

  private startNewSpeech(resetToBeginning: boolean = true) {
    /*
     * The session is retired before the queue is cleared, not after.
     *
     * cancel() ends every utterance outstanding - two of them now - and each one
     * calls back. Bumping first means those callbacks are already stale when they
     * arrive, whether the engine delivers them synchronously or on a later task.
     */
    this.speechSessionId += 1
    speechSynthesis.cancel()
    this.clearQueue()

    // Clear paused state and optionally reset to beginning
    if (resetToBeginning) {
      this.updateState({
        isPaused: false,
        currentSentenceIndex: 0,
        currentWordIndex: -1
      })
    } else {
      this.updateState({ isPaused: false })
    }

    // Split text into chunks - this is fast, just finds boundaries.
    // The actual TTS text cleaning happens lazily when each utterance is created.
    if (this.sentences.length === 0) {
      this.sentences = createSentenceUtterances(this.state.currentDisplayText, this.state.language)
      this.alignments.clear()
    }

    if (this.sentences.length === 0) {
      this.debug("No sentences to speak")
      return
    }

    // Ensure currentSentenceIndex is valid
    if (this.state.currentSentenceIndex < 0 || this.state.currentSentenceIndex >= this.sentences.length) {
      this.updateState({ currentSentenceIndex: 0 })
    }

    // Only the chunk to start from and the one behind it are built now; the rest
    // are created as the queue advances, so opening a long document stays cheap.
    if (!this.speakChunk(this.state.currentSentenceIndex)) {
      this.updateState({
        isPlaying: false,
        isPaused: false
      })
      return
    }

    this.queueAhead()
  }
}
